import React from "react";
import { Eye } from "lucide-react";

/** Phase J.1.4 — tiny relative-time formatter used by ViewedChip.
 *  Returns strings like "just now", "2h ago", "3d ago", "5w ago". For
 *  anything older than ~90 days we fall back to the ISO date so the chip
 *  doesn't lie. */
function relTime(iso) {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diff = Math.max(0, Date.now() - then);
    const sec = Math.round(diff / 1000);
    if (sec < 45) return "just now";
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 36) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 14) return `${day}d ago`;
    const wk = Math.round(day / 7);
    if (wk < 12) return `${wk}w ago`;
    return iso.slice(0, 10);
}

/**
 * Phase J.1.4 — secondary chip rendered next to the status pill on admin
 * invoice surfaces. Renders nothing when the invoice has never been
 * viewed (last_viewed_at is null), or when the parent status is one the
 * customer can't have seen (draft / void / paid-without-view).
 *
 * The "viewable" filter is intentionally permissive: we let the parent
 * gate the *render-or-not* decision against status, and only suppress
 * the chip here when ``at`` is missing.
 */
export default function ViewedChip({ at, by, className = "" }) {
    const label = relTime(at);
    if (!label) return null;
    const title = by ? `Viewed by ${by} at ${at}` : `Viewed at ${at}`;
    return (
        <span
            data-testid="invoice-viewed-chip"
            title={title}
            className={
                "inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-800 ring-1 ring-violet-200 " +
                "px-1.5 py-0.5 text-[10px] font-medium leading-none " + className
            }
        >
            <Eye size={10} aria-hidden /> Viewed {label}
        </span>
    );
}
